import ExclamationTriangle from "@/components/core/badges/ExclamationTriangle";
import { authenticationStore } from "@/state/auth/AuthStore";
import {
  resetTransaction,
  setTransactionFiles,
} from "@/state/transactions/CurrentTransactionEvents";
import { useCurrentTransaction } from "@/state/transactions/CurrentTransactionHook";
import { AuthenticationContext } from "@/type/core/auth/auth.types";
import { File } from "@/type/core/dashboard/dashboard.types";
import { Right } from "@/type/core/offices/offices.types";
import { ArrayHelper } from "@/utils/helpers/array-helper";
import ImportFilesCard from "@components/core/cards/ImportFilesCard";
import InfoModal from "@components/core/modals/InfoModal";
import { ROUTES } from "@routes/Routes";
import {
  MAX_SIZE_ALL_FILES,
  MAX_SIZE_PER_FILE,
  SCELLEMENT_MAX_FILES,
  SIGNATURE_MAX_FILES,
} from "@utils/Constant";
import { NEW_REQUEST_TYPE } from "@utils/enums/new.request.enum";
import { useUnit } from "effector-react";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface NewRequestSectionProps {
  selectedOfficeIds: string[];
  certificateDegradedMode?: boolean;
  signatureDegradedMode?: boolean;
  sealDegradedMode?: boolean;
  className?: string;
}

const NewRequestSection: FunctionComponent<NewRequestSectionProps> = ({
  selectedOfficeIds,
  certificateDegradedMode = false,
  signatureDegradedMode = false,
  sealDegradedMode = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);
  const me = authStore?.user;

  const { files } = useCurrentTransaction();

  const rights = me?.rights;

  const signatureUrl = ROUTES.extranet.transactions.signature.generate();
  const scellementUrl = ROUTES.extranet.transactions.scellement.generate();

  const noOfficeSelected = !selectedOfficeIds || selectedOfficeIds.length === 0;
  const showSign =
    rights &&
    (["SIGNATURE_BROUILLON_CREER"] as Right[]).some((right) =>
      rights.includes(right),
    );
  const showSeal =
    rights &&
    (["SCELLEMENT_BROUILLON_CREER"] as Right[]).some((right) =>
      rights.includes(right),
    );

  const formatDegradedText = () => {
    const degradedServices = [];
    if (certificateDegradedMode) {
      degradedServices.push(t("home.newRequest.certificateDegradedMode"));
    }
    if (signatureDegradedMode) {
      degradedServices.push(t("home.newRequest.signatureDegradedMode"));
    }
    if (sealDegradedMode) {
      degradedServices.push(t("home.newRequest.sealDegradedMode"));
    }
    let service = "";
    for (let i = 0; i < degradedServices.length; i++) {
      if (i === 0) {
        service += degradedServices[i];
      } else if (i === degradedServices.length - 1) {
        service += ` et ${degradedServices[i]}`;
      } else {
        service += `, ${degradedServices[i]}`;
      }
    }
    let res = t("home.newRequest.cardServiceUnavailable", { service });
    if (degradedServices.length > 1) {
      res = t("home.newRequest.cardServicesUnavailable", { service });
    }
    if (!certificateDegradedMode) {
      res += t("home.newRequest.cardServiceUnavailable2");
    } else {
      res += ".";
    }
    return res;
  };

  const handleSetFiles = (selectedFiles: File[], url: string) => {
    resetTransaction();
    setTransactionFiles(selectedFiles);
    navigate(url);
  };

  const handleImportFilesCardClick = (url: string) => {
    resetTransaction();
    navigate(url);
  };

  return (
    <div className={`app-section-new-request ${className}`}>
      {noOfficeSelected && (
        <div className="d-flex justify-content-center mb-16">
          <ExclamationTriangle
            text={t("home.newRequest.noOffice")}
            className="mb-8"
          />
        </div>
      )}
      {(certificateDegradedMode ||
        signatureDegradedMode ||
        sealDegradedMode) && (
        <div className="d-flex justify-content-center mb-16">
          <ExclamationTriangle text={formatDegradedText()} className="mb-8" />
        </div>
      )}
      <div className="new-request-items">
        <div className="new-request-item">
          <ImportFilesCard
            disabled={!showSign || certificateDegradedMode}
            title={t("home.newRequest.signature.title")}
            description={t("home.newRequest.signature.description")}
            descriptionSeparator={t("home.newRequest.signature.separator")}
            descriptionUrlTitle={t("home.newRequest.signature.urlTitle")}
            descriptionUrlTitle2={t("home.newRequest.signature.urlTitle2")}
            url={signatureUrl}
            accept="application/pdf"
            maxSize={MAX_SIZE_PER_FILE}
            maxSizeOfAllFiles={MAX_SIZE_ALL_FILES}
            name={NEW_REQUEST_TYPE.signature}
            setFiles={(files) => handleSetFiles(files, signatureUrl)}
            onClick={() => handleImportFilesCardClick(signatureUrl)}
            extensions={["pdf"]}
            transactionType="signature"
            currentDocumentsSize={ArrayHelper.sumElements(
              files.map((file) => file.size ?? 0),
            )}
            currentFileCount={files.length}
            maxCount={SIGNATURE_MAX_FILES}
          />
        </div>
        <div className="new-request-items-separator">
          <span>{t("common.or")}</span>
        </div>
        <div className="new-request-item">
          <ImportFilesCard
            disabled={!showSeal}
            title={t("home.newRequest.scellement.title")}
            description={t("home.newRequest.scellement.description")}
            descriptionSeparator={t("home.newRequest.scellement.separator")}
            descriptionUrlTitle={t("home.newRequest.scellement.urlTitle")}
            descriptionUrlTitle2={t("home.newRequest.scellement.urlTitle2")}
            maxSize={MAX_SIZE_PER_FILE}
            maxSizeOfAllFiles={MAX_SIZE_ALL_FILES}
            url={scellementUrl}
            accept="application/pdf"
            name={NEW_REQUEST_TYPE.scellement}
            setFiles={(files) => handleSetFiles(files, scellementUrl)}
            onClick={() => handleImportFilesCardClick(scellementUrl)}
            extensions={["pdf"]}
            transactionType="scellement"
            currentDocumentsSize={ArrayHelper.sumElements(
              files.map((file) => file.size ?? 0),
            )}
            currentFileCount={files.length}
            maxCount={SCELLEMENT_MAX_FILES}
          />
        </div>
      </div>
      <div className="d-flex justify-content-center mb-16">
        <div className="text-primary">
          {t("home.newRequest.pdfsCompatibilityWarning")}
          <InfoModal value="pdfsCompatibility" info />
        </div>
      </div>
    </div>
  );
};

export default NewRequestSection;
